/**
 * @file Shortbread Context for AWS Cookie Consent Management
 *
 * This provides a React context and hook for managing AWS Shortbread cookie consent.
 * Shortbread handles GDPR compliance by showing cookie consent banners to EU users
 * and providing cookie preference management.
 *
 * Example grabbed from https://code.amazon.com/packages/AWSGaiaFrontendComponents/blobs/mainline/--/src/contexts/shortbread/shortbread.tsx
 */

import { AWSCShortbread } from "@amzn/shortbread";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Cookie categories supported by Shortbread
 */
export type CookieCategory =
  | "essential"
  | "functional"
  | "performance"
  | "advertising";

export const COOKIE_CATEGORIES: CookieCategory[] = [
  "essential",
  "functional",
  "performance",
  "advertising",
];

/**
 * Shortbread instance interface (simplified for our needs)
 */
export interface ShortbreadInstance {
  /** Show the cookie consent banner (for EU users) */
  checkForCookieConsent: () => void;
  /** Open the cookie preferences modal */
  customizeCookies: () => void;
  /** Check if user has consented to a specific cookie category */
  hasConsent: (category: CookieCategory) => boolean;
}

/**
 * Shortbread context value interface
 */
export interface ShortbreadContextValue extends ShortbreadInstance {
  /** Whether Shortbread has been successfully initialized */
  isInitialized: boolean;
}

/**
 * Shortbread Context usable by consumers to provide Shortbread once initialized for their service.
 */
export const ShortbreadContext = createContext<ShortbreadContextValue | null>(
  null
);

/**
 * Run a Shortbread function and handle errors. This can happen when shortbread has no registry.
 */
export function runShortbreadFunction<T>(
  action: string,
  shortbreadFunction: () => T
): T | undefined {
  try {
    return shortbreadFunction();
  } catch (error) {
    return undefined;
  }
}

/**
 * Hook to access Shortbread functionality
 *
 * @returns The Shortbread context value
 * @throws Error if used outside of a ShortbreadContext Provider
 */
export function useShortbread(): ShortbreadContextValue {
  const context = useContext(ShortbreadContext);

  if (context === null) {
    throw new Error(
      "useShortbread must be used within a provided ShortbreadContext."
    );
  }

  return context;
}

/**
 * Hook to check cookie consent for a specific category
 *
 * @param category - The cookie category to check
 * @returns Whether the user has consented to this category
 */
export function useCookieConsent(category: CookieCategory): boolean {
  const { hasConsent } = useShortbread();

  return hasConsent(category);
}

/**
 * Initialize Shortbread instance
 */
function initializeShortbread(
  onConsentChanged?: () => void
): ShortbreadInstance | null {
  try {
    return AWSCShortbread({
      domain: window.location.hostname,
      // Add any additional configuration options here
      onConsentChanged,
      // The Shortbread registry represents cookies -> category, but we want this framework to be generic,
      // so we will treat the registry as a category -> category lookup instead, allowing us to more easily
      // see if a category is enabled, and track when that changes. This approach was validated by an
      // engineer in the shortbread team.
      registry: Object.fromEntries(
        COOKIE_CATEGORIES.map((category) => [category, { category }])
      ),
    }) as ShortbreadInstance;
  } catch (error) {
    return null;
  }
}

interface ShortbreadProviderProps {
  children: ReactNode;
}

/**
 * Shortbread Provider Component
 *
 * Initializes Shortbread and provides it to all child components via context.
 * Handles the async initialization and provides fallback behavior when Shortbread is unavailable.
 */
export function ShortbreadProvider({
  children,
}: ShortbreadProviderProps): JSX.Element {
  const [shortbread, setShortbread] = useState<ShortbreadInstance | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // we need to track the last update datetime from shortbread to ensure that consumers are re-triggered.
  const [lastUpdateDatetime, setLastUpdateDatetime] = useState(Date.now());

  useEffect(() => {
    const instance = initializeShortbread(() => {
      setLastUpdateDatetime(Date.now());
    });

    setShortbread(instance);
    setIsInitialized(true);
  }, []);

  const { checkForCookieConsent, customizeCookies } = useMemo(
    () => ({
      checkForCookieConsent: () => {
        runShortbreadFunction("checkForCookieConsent", () =>
          shortbread?.checkForCookieConsent()
        );
      },
      customizeCookies: () => {
        runShortbreadFunction("customizeCookies", () =>
          shortbread?.customizeCookies()
        );
      },
    }),
    [shortbread]
  );

  // unlike the two callbacks above, we need this function to be re-generated every time the cookie preferences
  // are updated.
  const hasConsent = useCallback(
    (category: CookieCategory) => {
      // always allow essential cookies
      if (category === "essential") {
        return true;
      }

      const hasConsent = runShortbreadFunction("hasConsent", () =>
        shortbread?.hasConsent(category)
      );

      return Boolean(hasConsent);

      // the lastUpdateDatetime dependency is here to cause a regeneration of this callback when the cookie
      // preferences are updated. It is technically not used internally, but we still want a new function to
      // be created.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [lastUpdateDatetime, shortbread]
  );

  const contextValue = useMemo<ShortbreadContextValue>(() => {
    return {
      checkForCookieConsent,
      customizeCookies,
      hasConsent,
      isInitialized,
    };
  }, [checkForCookieConsent, customizeCookies, hasConsent, isInitialized]);

  return (
    <ShortbreadContext.Provider value={contextValue}>
      {children}
    </ShortbreadContext.Provider>
  );
}
